import timezones from "@/helpers/timezones";
import { getDateTokens, type OrgDateFormat } from "@/helpers/dateFormat";

/**
 * 8-4-4-4-12 hex, case-insensitive. Every machine-minted upload name carries one: a mobile capture
 * (`<uuid>.jpg`), a snapshot (`snapshot-<uuid>.jpg`), and the key basename Contact API mints for
 * every upload (`file.<conversationId>.<uuid>.jpg`).
 */
const UPLOADED_FILE_NAME_UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * True when the name was minted by a machine rather than chosen by a person, so it must never reach
 * the screen. Contact API (`realUploadedFileNameOrNull` in helpers/files.ts) and Contact Mobile
 * (`isMachineMintedUploadName` in UploadedFileDisplayName.kt) apply the same rule; the two apps
 * share no code, so the rule is deliberately duplicated rather than imported.
 */
function isMachineMintedUploadName(name: string): boolean {
	// An S3 object key is a path; a name a person chose never is.
	if (name.includes("/") || name.includes("\\")) return true;
	// A bare extension (".jpg") is not a name anybody would recognize as their file.
	const lastDotIndex = name.lastIndexOf(".");
	const nameWithoutExtension = lastDotIndex === -1 ? name : name.slice(0, lastDotIndex);
	if (!nameWithoutExtension.trim()) return true;
	// Judged on everything before the extension, the same way Contact API and Contact Mobile do it.
	return UPLOADED_FILE_NAME_UUID_PATTERN.test(nameWithoutExtension);
}

/** Characters that are not valid in a saved file name; the created-date fallback contains "/" and ":". */
const PATH_UNSAFE_CHARACTERS = /[/\\:*?"<>|]/g;

/** The org date formats a display date could have been rendered under; [OrgDateFormat] has only these. */
const ORG_DATE_FORMATS: OrgDateFormat[] = ["MDY", "DMY"];

/**
 * Every rendering of this file's created date-time the display rule could have produced, matching
 * timezones.formatDate's own default style for each org format.
 *
 * Both formats, deliberately, not just the current one: the name handed to these helpers was
 * rendered at some earlier moment, and the org's MDY/DMY choice can move in between — an admin
 * saving it (OrganizationDetails) re-points formatDate while already-rendered rows keep the old
 * string, and the module default is "MDY" until getMe seeds the real value after the first paint.
 * Keyed on the current format alone, the guard below would miss and the date would read as a real
 * name again, which is the whole defect it exists to stop.
 */
function getRenderedCreatedDates(file: any): string[] {
	const createdAt = file?.createdAt || file?.created;
	if (!createdAt) return [];
	return ORG_DATE_FORMATS.map((format) =>
		String(timezones.formatDate(createdAt, `${getDateTokens(format).dateShortMonth} h:mm A z`) || "").trim()
	).filter(Boolean);
}

/**
 * True when the candidate is nothing but this file's own created date read back — raw, or already
 * swapped to path-safe characters by a previous trip through [getDownloadFileName].
 */
function isRenderedCreatedDate(candidate: string, renderedCreatedDates: string[]): boolean {
	return renderedCreatedDates.some(
		(rendered) => candidate === rendered || candidate === rendered.replace(PATH_UNSAFE_CHARACTERS, "-")
	);
}

/** The trimmed real uploaded file name, or null when this candidate is not one. */
function realUploadedFileNameOrNull(candidate: unknown): string | null {
	if (typeof candidate !== "string") return null;
	const trimmedName = candidate.trim();
	if (!trimmedName || isMachineMintedUploadName(trimmedName)) return null;
	return trimmedName;
}

function getRealUploadedFileName(file: any): string | null {
	// Resolved lazily: formatDate is moment-backed and this runs per file in list-wide counts.
	let renderedCreatedDates: string[] | undefined;
	const nameCandidates = [file?.uploadResult?.metadata?.filename, file?.title, file?.name, file?.fileName];
	for (const candidate of nameCandidates) {
		const realFileName = realUploadedFileNameOrNull(candidate);
		if (!realFileName) continue;
		// Every Admin download call site renders the display name first and then hands it straight
		// back as `name` (PDFViewerDialog, EntityDocuments and DocumentItem all do). The month-name
		// format LSP-19482 moved to — "Jun 24 2025 3:41 PM PDT" — carries no path separator and no
		// UUID, so the rules above read it as a name a person chose; the download then keeps the date
		// and loses its extension. A date that is only this file's own createdAt is not a real name.
		if (renderedCreatedDates === undefined) renderedCreatedDates = getRenderedCreatedDates(file);
		if (isRenderedCreatedDate(realFileName, renderedCreatedDates)) continue;
		return realFileName;
	}
	return null;
}

/**
 * The one rule for showing an uploaded file's name: the real uploaded file name when we have it,
 * otherwise the item's created date-time in the org's standard format. Never the S3 key or its UUID
 * basename — the API no longer falls back to the key, so `name`/`fileName`/`title` can arrive null
 * and must render as a date rather than blank. `fileName` is the API's File virtual, and `created`
 * is the search index's spelling of `createdAt` (see IndexableDocument).
 */
export function getDisplayFileName(file: any): string {
	return getRealUploadedFileName(file) || timezones.formatDate(file?.createdAt || file?.created) || "";
}

const FILE_EXTENSION_PATTERN = /^[a-z0-9]+$/;

function getExtensionFromPath(path: string): string {
	// A signed key or URL carries a query string or fragment, and only the last path segment can hold an extension.
	const lastPathSegment = String(path || "")
		.split(/[?#]/)[0]
		.split("/")
		.pop();
	const parts = String(lastPathSegment || "").split(".");
	const candidate = parts.length > 1 ? (parts[parts.length - 1] || "").toLowerCase() : "";
	return FILE_EXTENSION_PATTERN.test(candidate) ? candidate : "";
}

export function getFileExtension(file: any): string {
	const explicitExtension = typeof file?.extension === "string" ? file.extension.replace(/^\./, "").toLowerCase() : "";
	if (explicitExtension) return FILE_EXTENSION_PATTERN.test(explicitExtension) ? explicitExtension : "";

	const key = file?.uploadResult?.key || file?.uploadResult?.Key || "";
	// `key` wins when present, so rendering the display name first would be wasted work on every
	// file of a list-wide count — formatDate is moment-backed.
	if (key) return getExtensionFromPath(key);
	return getExtensionFromPath(getDisplayFileName(file) || "");
}

/**
 * Name to save a download under. A real uploaded file name is kept exactly as the person named it.
 * The created-date fallback has no extension by construction, so it gets one from the file itself
 * or, failing that, from the media URL, and a date-named document still downloads as a usable file.
 */
export function getDownloadFileName(file: any, mediaUrl?: string): string {
	const realFileName = getRealUploadedFileName(file);
	if (realFileName) return realFileName.replace(PATH_UNSAFE_CHARACTERS, "-");

	const dateName = String(timezones.formatDate(file?.createdAt || file?.created) || "").replace(PATH_UNSAFE_CHARACTERS, "-");
	if (!dateName) return "";
	const extension = getFileExtension(file) || getExtensionFromPath(String(mediaUrl || ""));
	return extension ? `${dateName}.${extension}` : dateName;
}

function isSnapshotMasterDocument(file: any): boolean {
	const documentRelatedEntity = file?.documentRelatedEntity || file?.MasterDocument?.documentRelatedEntity || file?.entityType;
	return typeof documentRelatedEntity === "string" && documentRelatedEntity.toLowerCase() === "snapshot";
}

/**
 * The name Scribe's own `isPdfFile` read before [getFileExtension] existed: the first name the
 * upload actually carries, then the S3 key's basename. Unlike the display rule a machine-minted
 * name belongs here — "<uuid>.pdf" must never reach the screen, but it still says this is a PDF.
 * Deliberately the FIRST candidate rather than any of them: testing all would newly call a file a
 * PDF because some other field happens to end ".pdf", which the rule this restores never did.
 */
function getUploadedNameForType(file: any): string {
	const nameCandidates = [file?.uploadResult?.metadata?.filename, file?.title, file?.name, file?.fileName];
	for (const candidate of nameCandidates) {
		if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
	}
	const key = file?.uploadResult?.key || file?.uploadResult?.Key || "";
	return String(key).split("/").pop() || "";
}

/**
 * The one rule for "is this upload a PDF", shared by the Scribe ready/selected counts and the file
 * list Scribe actually sends — two answers to this question is how a document comes to be counted
 * but never analyzed. The mime type settles it when present, then the file's own extension, then
 * [getUploadedNameForType] ending ".pdf". [getFileExtension] alone is not enough: it reads the S3
 * key first, so a PDF uploaded as "image/jpeg" (key "…<uuid>.jpg", name "estimate.pdf") reads "jpg".
 */
export function isPdfUpload(file: any): boolean {
	// A list view's row records this verdict from the raw API file it was built from
	// (EntityDocuments.mapFilesToRows) and then drops `uploadResult`, so the mime type and key the
	// checks below need are gone by the time the row is counted. Trust the recorded answer: without
	// this, the same document reads as a PDF from the raw list and as a non-PDF from its own row.
	if (typeof file?.isPdf === "boolean") return file.isPdf;

	const mimeType = file?.uploadResult?.metadata?.filetype || file?.uploadResult?.metadata?.fileType || file?.mimeType || "";
	// Split the parameters off first: "application/pdf; charset=binary" is still a PDF.
	if (typeof mimeType === "string" && mimeType.split(";")[0].trim().toLowerCase() === "application/pdf") return true;
	if (getFileExtension(file) === "pdf") return true;
	return getUploadedNameForType(file).toLowerCase().split(/[?#]/)[0].endsWith(".pdf");
}

export function countReadyPdfDocuments(files: any[] = []): number {
	return (files || []).filter((file) => !isSnapshotMasterDocument(file) && isPdfUpload(file)).length;
}

export function getDisplayCreatedBy(file: any, fallbackUser?: any): string {
	const user = file?.User || fallbackUser;
	if (!user) return "";
	const fullName = (user.name || [user.firstname, user.lastname].filter(Boolean).join(" ")).trim();
	return fullName || user.email || "";
}
